// Condense brief: the scaffold-grounded keep/drop guidance the watchdog hands
// the agent when context fill crosses the threshold. Deterministic — assembled
// from the event log + scaffold structure, no AI summarization.
//
// Reuses session.ts (discoverContextFiles) rather than re-deriving context
// selection. Brief shape is AIAgentMinder's three fields: decisions / next / blockers.

import type { CaiConfig } from "../types.js";
import { readEvents } from "../events.js";
import { discoverContextFiles } from "../session.js";

export interface CondenseBrief {
  decisions: string[];
  nextStep: string | null;
  blockers: string[];
  keep: string[];
  drop: string[];
  /** context/ files to reload instead of carrying their content in history. */
  reload: string[];
}

export function buildCondenseBrief(config: CaiConfig): CondenseBrief {
  const events = readEvents(config);

  const decisions = events.filter((e) => e.kind === "decision").map((e) => e.message);
  const blockers = events.filter((e) => e.kind === "risk").map((e) => e.message);
  const todos = events.filter((e) => e.kind === "todo").map((e) => e.message);
  // Next step = the most recently logged open todo, if any.
  const nextStep = todos.length ? todos[todos.length - 1] : null;

  const reload = discoverContextFiles(config.scaffoldRoot).map((c) => `context/${c.filename}`);

  return {
    decisions,
    nextStep,
    blockers,
    keep: [
      "CLAUDE.md / ROUTER.md (always-loaded anchors)",
      "the current task and its next step",
      "open decisions and blockers below",
    ],
    drop: [
      "completed-task back-and-forth",
      "stale tool output and file dumps already acted on",
      "context/ file contents — reload them instead (see below)",
    ],
    reload,
  };
}

/** Render the brief as text for printing or hook injection. */
export function renderBrief(brief: CondenseBrief): string {
  const parts: string[] = ["# Condense brief", ""];

  parts.push("## Keep");
  parts.push(...brief.keep.map((k) => `- ${k}`));
  parts.push("");

  parts.push("## Drop");
  parts.push(...brief.drop.map((d) => `- ${d}`));
  parts.push("");

  parts.push("## Decisions");
  parts.push(brief.decisions.length ? brief.decisions.map((d) => `- ${d}`).join("\n") : "- (none logged)");
  parts.push("");

  parts.push("## Next step");
  parts.push(brief.nextStep ? `- ${brief.nextStep}` : "- (none logged)");
  parts.push("");

  parts.push("## Blockers");
  parts.push(brief.blockers.length ? brief.blockers.map((b) => `- ${b}`).join("\n") : "- (none logged)");
  parts.push("");

  if (brief.reload.length) {
    parts.push("## Reload instead of carrying in history");
    parts.push(...brief.reload.map((r) => `- ${r}`));
  }

  return parts.join("\n");
}
